// Empresa (tenant) atendida por este cardápio.
// A API é multi-empresa: o token guest carrega a claim `company_code` com este UUID
// e todas as rotas (produtos, categorias, pedidos, PIX, frete...) são escopadas por ela.
// Ver docs/api.md, seções 1.2 e 1.4.
//
// Configure em REACT_APP_COMPANY_CODE (.env*). Como toda variável do CRA, só é lida
// no `yarn start`/`yarn build`: depois de trocar, reinicie o dev server ou gere o build de novo.

// Bonfim Gourmet — usada quando a variável não existe (mantém o deploy atual funcionando)
const DEFAULT_COMPANY_CODE = "76043312-19c6-4062-bb76-fd744c468591";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const resolveCompanyCode = () => {
  const fromEnv = String(process.env.REACT_APP_COMPANY_CODE || "").trim();
  if (!fromEnv) {
    throw new Error(
      "❌ REACT_APP_COMPANY_CODE não definido: configure o UUID da empresa no .env (ver .env.example) e reinicie o start/build.",
    );
  }
  if (!UUID_RE.test(fromEnv)) {
    console.error(
      `❌ REACT_APP_COMPANY_CODE inválido ("${fromEnv}"): esperado um UUID. Usando a empresa padrão.`,
    );
  }
  return fromEnv.toLowerCase();
};

export const COMPANY_KEY = resolveCompanyCode();

// Chave que guarda no navegador a empresa dona dos dados abaixo
const STORAGE_COMPANY_KEY = "bonfim_company_code";

// Dados do navegador que pertencem a uma empresa (token, carrinho, checkout, pedido)
const COMPANY_SCOPED_KEYS = [
  "guest_token",
  "bonfim_cart",
  "bonfim_checkout_data",
  "bonfim_products_tracking",
  "bonfim_products_tracking_detailed",
  "bonfim_company_branding", // logo/nome para favicon e título (lib/branding.js)
];

/**
 * Se o navegador tem dados de outra empresa, descarta token, carrinho, checkout e
 * pedido em acompanhamento (docs/api.md seção 4: ao trocar de loja, descarte os caches).
 * Deve rodar antes do primeiro render, pois a App lê o carrinho do localStorage ao montar.
 */
export const resetStorageIfCompanyChanged = () => {
  try {
    const stored = localStorage.getItem(STORAGE_COMPANY_KEY);

    if (stored && stored !== COMPANY_KEY) {
      const orderKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("pedido.")) orderKeys.push(key);
      }
      [...COMPANY_SCOPED_KEYS, ...orderKeys].forEach((key) =>
        localStorage.removeItem(key),
      );
      console.log(
        "🔄 Empresa alterada: dados locais da empresa anterior descartados",
      );
    }

    localStorage.setItem(STORAGE_COMPANY_KEY, COMPANY_KEY);
  } catch (e) {
    // localStorage indisponível (modo privado/bloqueado): segue sem limpar
  }
};

/**
 * Lê a claim `company_code` do payload do JWT (sem validar assinatura).
 * Retorna null se o token for inválido ou não tiver a claim.
 */
export const getTokenCompanyCode = (token) => {
  try {
    return decodeJwtPayload(token)?.company_code || null;
  } catch (e) {
    return null;
  }
};

/**
 * Decodifica o payload do JWT (base64url). Lança erro se o token for inválido.
 */
export const decodeJwtPayload = (token) => {
  const base64 = String(token)
    .split(".")[1]
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return JSON.parse(atob(base64));
};

/**
 * Indica se o token pertence a esta empresa e ainda não expirou.
 */
export const isTokenValidForCompany = (token) => {
  try {
    const payload = decodeJwtPayload(token);
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp <= now) return false;
    return payload.company_code === COMPANY_KEY;
  } catch (e) {
    return false;
  }
};
